import { CapacityTagDto } from 'Models/Shared/Capacity/CapacityTagDto';
import { appConfigStore } from 'Models/AppConfigStore';

/**
 * Ordering for the capacities shown on a trip card (ferry schedule + ticket selection).
 *
 * Statics (vehicle / trailer / passenger / freight) get their order from app config; dynamic
 * capacity types use their own assigned `order`. Everything is merged and sorted by that single
 * number. Rules:
 *  - items with an explicit order come before dynamic types that have no order (those render last)
 *  - lower order shows first
 *  - on a tie, a dynamic capacity type renders before a static one
 *  - otherwise insertion order is preserved (Array.prototype.sort is stable)
 */
export interface OrderedCapacityItem<T> {
	/** Resolved order — config order for statics, `tag.order` for dynamic types. */
	order: number;
	/** True for dynamic capacity types; false for the static capacities. */
	isDynamic: boolean;
	/** False for a dynamic type with no order assigned (sorts to the end). */
	hasOrder: boolean;
	item: T;
}

export function sortOrderedCapacities<T>(items: OrderedCapacityItem<T>[]): T[] {
	return [...items]
		.sort((a, b) => {
			if (a.hasOrder !== b.hasOrder) {
				return a.hasOrder ? -1 : 1;
			}
			if (a.order !== b.order) {
				return a.order - b.order;
			}
			if (a.isDynamic !== b.isDynamic) {
				return a.isDynamic ? -1 : 1;
			}
			return 0;
		})
		.map(x => x.item);
}

/**
 * Separates the passenger classes from the rest of the capacity tags.
 *
 * <p>A class is a breakdown of the passenger capacity, not a capacity alongside it, so the classes
 * are drawn as one block at the passenger position (`capacityOrderPassenger`) and ordered among
 * themselves by their own order — rather than each class taking its own place in the row and being
 * scattered between vehicles, trailers and the dynamic capacity types.</p>
 *
 * <p>With fare types switched off there are no class tags at all, whatever the server sent. The
 * flag is a client-side gate — no server code reads it — so the capacity engine goes on counting
 * classes and the tags go on arriving. A site with the feature off was therefore still showing a
 * chip per fare type on every trip card, each one titled with that fare type's name, while the rest
 * of the wizard showed nothing about fare types at all. Dropped here rather than at each of the two
 * call sites, so a third one cannot reintroduce it.</p>
 *
 * <p>`showOnTripCard` is applied here too, and only here. The server sends a tag for every class,
 * shown or not, because the wizard's fare columns need the numbers behind a class it still sells —
 * a fare with no capacity tag reads as available, so a hidden class could never sell out. The cards
 * are the one surface the flag is about, so they are the one place it is honoured.</p>
 */
export function splitPassengerClassTags(tags: CapacityTagDto[]): {
	passengerClassTags: CapacityTagDto[];
	otherTags: CapacityTagDto[];
} {
	return {
		passengerClassTags: appConfigStore.passengerClassesEnabled
			? tags
				.filter(tag => tag.category === 'PassengerClass' && tag.showOnTripCard !== false)
				.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
			: [],
		otherTags: tags.filter(tag => tag.category !== 'PassengerClass'),
	};
}
